import { useCallback } from 'react';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useClaimAttention } from '../../../store';

export type ClaimDispositionSource =
  | 'claims_list'
  | 'work_queue'
  | 'overview'
  | 'claims_review'
  /* No caller since GDN-3380 removed the Encounters row items. Left in the
     union so historical Statsig data keeps a name to match against; it goes
     with the rest of this file. */
  | 'encounter_worklist'
  | 'encounter_claim';

interface UseClaimDispositionOptions {
  /** Where the disposition was triggered — flows through to Statsig. */
  source: ClaimDispositionSource;
  /**
   * Called after a SUCCESSFUL dismiss/snooze (e.g. advance selection).
   *
   * The action is passed because the two are different events to whoever is
   * listening — the claim queue reports which one resolved a claim's place
   * (GDN-3322), and a shared callback that could not say which would force
   * every caller to keep its own copy of what it just asked for.
   */
  onDispositioned?: (action: 'dismiss' | 'snooze') => void;
  attentionLane?: string;
  signals?: string[];
}

/** Pull an `error_code` out of an RTK Query error payload, if present. */
function extractErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const data = (error as Record<string, unknown>).data;
  if (data && typeof data === 'object') {
    return (data as { error_code?: string }).error_code;
  }
  return undefined;
}

/**
 * @deprecated Replaced by `features/billing/hooks/useEncounterWorkDisposition`
 * (GDN-3380). That hook is keyed by ENCOUNTER, writes a real
 * `encounter_work_dispositions` row the queues read back, and carries Undo and
 * a note; this one writes a claim-keyed JSONB blob with no snooze actor that
 * only two readers consult.
 *
 * KEPT, NOT DELETED, and this is the whole reason: three callers remain and
 * none of them is packet D's to switch.
 *
 *   - `components/EncounterDetail/UnifiedEncounterWorkspace.tsx` — live, the
 *     encounter-detail claim sub-tab. `source: 'encounter_claim'` has no
 *     equivalent in the new contract's three sources, so switching it is a
 *     product decision, not a rename.
 *   - `components/v2/claims-review/ClaimCaseActions.tsx` and
 *     `components/v2/claims/ClaimRowActions.tsx` — reachable only through
 *     LegacyClaimsTab, i.e. only with `feature_jotbill_claim_workbench` off.
 *
 * Two further callers, `v2/overview/OverviewTab.tsx` and `v2/work/WorkTab.tsx`,
 * are unreachable: nothing imports their barrels. They are dead code that
 * happens to compile, and deleting them is a separate change.
 *
 * The `billing_claim_attention_*` events stay for the same reason. Retire this
 * file, those constants, and `useClaimAttention` together, in the change that
 * removes the last caller.
 *
 * Shared dismiss / "remind me tomorrow" behavior for a claim, with Statsig
 * tracking and failure snackbars baked in.
 */
export function useClaimDisposition({
  source,
  onDispositioned,
  attentionLane,
  signals,
}: UseClaimDispositionOptions) {
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();
  const { dismiss: dismissClaim, remindTomorrow: snoozeClaim, isUpdating } = useClaimAttention();

  const dismiss = useCallback(
    async (claimId: string) => {
      const result = await dismissClaim(claimId);
      if (result.success) {
        billing.claimAttentionDismissed(claimId, {
          source,
          attention_lane: attentionLane,
          signals,
        });
        onDispositioned?.('dismiss');
      } else {
        billing.claimAttentionFailed(claimId, {
          action: 'dismiss',
          source,
          error_code: extractErrorCode(result.error),
        });
        enqueueSnackbar('Failed to dismiss', { variant: 'error' });
      }
    },
    [dismissClaim, billing, source, attentionLane, signals, onDispositioned, enqueueSnackbar],
  );

  const remindTomorrow = useCallback(
    async (claimId: string) => {
      const result = await snoozeClaim(claimId);
      if (result.success) {
        billing.claimAttentionSnoozed(claimId, {
          source,
          attention_lane: attentionLane,
          signals,
        });
        onDispositioned?.('snooze');
      } else {
        billing.claimAttentionFailed(claimId, {
          action: 'snooze',
          source,
          error_code: extractErrorCode(result.error),
        });
        enqueueSnackbar('Failed to snooze', { variant: 'error' });
      }
    },
    [snoozeClaim, billing, source, attentionLane, signals, onDispositioned, enqueueSnackbar],
  );

  return { dismiss, remindTomorrow, isUpdating };
}
